import { useSearchParams } from 'react-router-dom';
import { FilterSearchParam } from '../../../../../../shared-components/elementis/types/FilterSearchParam';
import { useCallback, useMemo } from 'react';
import { SchoolYearValue, useResultsDataContext } from '../../ResultsDataContext';
import { Module } from '../../../../../../api/module';
import { SupportedUserLanguage } from '@pelckmans/business-components/context/LanguageContext';
import { buildRelativePath } from '../../../../../../hooks/useRoutes';

export function createLinkWithScorableItemToFilter(scorableItemIds: string[], language: SupportedUserLanguage) {
  const url = buildRelativePath('MATERIAL.RESULTS.BY_ITEM', language);
  const search = new URLSearchParams(window.location.search);

  search.set(FilterSearchParam.Exercises, scorableItemIds.join(','));

  return `../${url}?${search.toString()}`;
}

interface Opts {
  selectedModule: Module | null;
  selectedSchoolYear: SchoolYearValue | null;
}

export function useSelectedScorableItems({ selectedModule, selectedSchoolYear }: Opts) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { scorableItemsPerSchoolYearPerModule } = useResultsDataContext();

  const scorableItemsInSchoolYearOfModule = useMemo(() => {
    if (!selectedModule || !selectedSchoolYear) return [];

    return scorableItemsPerSchoolYearPerModule[selectedSchoolYear.id]?.[selectedModule.id] || [];
  }, [scorableItemsPerSchoolYearPerModule, selectedModule, selectedSchoolYear]);

  const spScorableItemIds = useMemo(
    () => searchParams.get(FilterSearchParam.Exercises)?.split(',') || [],
    [searchParams],
  );

  const selectedScorableItems = useMemo(() => {
    if (!selectedModule || !selectedSchoolYear) return [];

    if (spScorableItemIds.length === 0) {
      return scorableItemsInSchoolYearOfModule;
    }

    return scorableItemsInSchoolYearOfModule.filter(scorableItem =>
      spScorableItemIds.some(id => scorableItem.id === id),
    );
  }, [scorableItemsInSchoolYearOfModule, selectedModule, selectedSchoolYear, spScorableItemIds]);

  const setSelectedScorableItems = useCallback(
    (itemIds: string[]) => {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);

        if (itemIds.length === 0 || scorableItemsInSchoolYearOfModule.length === 0) {
          newParams.delete(FilterSearchParam.Exercises);
        } else if (itemIds.length === 1) {
          newParams.set(FilterSearchParam.Exercises, itemIds[0]);
        } else if (itemIds.length === scorableItemsInSchoolYearOfModule.length) {
          newParams.delete(FilterSearchParam.Exercises);
        } else {
          newParams.set(FilterSearchParam.Exercises, itemIds.join(','));
        }

        return newParams;
      });
    },
    [scorableItemsInSchoolYearOfModule.length, setSearchParams],
  );

  return { selectedScorableItems, setSelectedScorableItems, spScorableItemIds };
}
